import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import {
  FaEye,
  FaSearch,
  FaFileExport,
  FaFilter,
  FaCalendarAlt,
  FaChartBar,
  FaColumns,
  FaDownload,
  FaPrint,
  FaSlidersH,
  FaTimes,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";
import useFetch from "../../../components/shared/useFetch";
import placeholderImage from "../../../images/placeholder.png";

const EventFlexiReportsChild = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [eventData, setEventData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [{ user }] = useAtom(gbState);

  // Report customization states
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedColumns, setSelectedColumns] = useState([
    "title",
    "event_type",
    "sponsorship_status",
    "event_location",
    "view_event_start_date",
    "view_event_end_date",
  ]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [sponsorshipFilter, setSponsorshipFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [showFilters, setShowFilters] = useState(false);
  const [chartType, setChartType] = useState("none"); // none, bar, pie
  const [chartMetric, setChartMetric] = useState("event_type"); // what to measure in charts

  // Available columns for selection
  const availableColumns = [
    { id: "title", label: "Event Title" },
    { id: "description", label: "Description" },
    { id: "event_type", label: "Event Type" },
    { id: "sponsorship_status", label: "Sponsorship Status" },
    { id: "event_location", label: "Location" },
    { id: "event_contact_number", label: "Contact Number" },
    { id: "view_event_start_date", label: "Start Date & Time" },
    { id: "view_event_end_date", label: "End Date & Time" },
    { id: "day_event", label: "Day Event" },
    { id: "zipcode", label: "Zipcode" },
  ];

  let urlLink = `${process.env.REACT_APP_API_URL}/event/${user.church_id}`;
  const { data, loading, error, refetch } = useFetch(urlLink);

  useEffect(() => {
    if (data?.events) {
      const updatedData = data.events.map((event) => ({
        ...event,
        id: event._id,
        view_event_start_date: event.event_start_date
          ? dayjs(event.event_start_date).format("MM-DD-YYYY") +
            " " +
            event.event_start_time
          : "",
        view_event_end_date: event.event_end_date
          ? dayjs(event.event_end_date).format("MM-DD-YYYY") +
            " " +
            event.event_end_time
          : "",
      }));
      setEventData(updatedData);
      setFilteredData(updatedData);
    }
  }, [data]);

  // Extract unique values for filters
  const uniqueLocations = useMemo(() => {
    return [...new Set(eventData.map((event) => event.event_location))].filter(
      Boolean
    );
  }, [eventData]);

  const uniqueEventTypes = useMemo(() => {
    return [
      ...new Set(eventData.map((event) => event.event_type?.label)),
    ].filter(Boolean);
  }, [eventData]);

  const uniqueSponsorshipStatuses = useMemo(() => {
    return [
      ...new Set(eventData.map((event) => event.sponsorship_status?.label)),
    ].filter(Boolean);
  }, [eventData]);
  // Apply filters and search
  useEffect(() => {
    let results = [...eventData];

    // Search filter
    if (searchTerm) {
      results = results.filter(
        (event) =>
          event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          event.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          event.event_location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dayjs(dateRange[0]);
      const endDate = dayjs(dateRange[1]);

      results = results.filter((event) => {
        const eventDate = dayjs(event.event_start_date);
        return eventDate.isAfter(startDate) && eventDate.isBefore(endDate);
      });
    }

    // Event type filter
    if (eventTypeFilter !== "all") {
      results = results.filter(
        (event) => event.event_type?.label === eventTypeFilter
      );
    }

    // Sponsorship status filter
    if (sponsorshipFilter !== "all") {
      results = results.filter(
        (event) => event.sponsorship_status?.label === sponsorshipFilter
      );
    }

    // Location filter
    if (locationFilter !== "all") {
      results = results.filter(
        (event) => event.event_location === locationFilter
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      results.sort((a, b) => {
        // Handle nested objects like event_type.label
        const keyParts = sortConfig.key.split(".");
        let aValue = a;
        let bValue = b;

        for (const part of keyParts) {
          aValue = aValue?.[part];
          bValue = bValue?.[part];
        }

        if (aValue === null || aValue === undefined)
          return sortConfig.direction === "ascending" ? -1 : 1;
        if (bValue === null || bValue === undefined)
          return sortConfig.direction === "ascending" ? 1 : -1;

        if (typeof aValue === "string") {
          return sortConfig.direction === "ascending"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else {
          return sortConfig.direction === "ascending"
            ? aValue - bValue
            : bValue - aValue;
        }
      });
    }

    setFilteredData(results);
  }, [
    eventData,
    searchTerm,
    dateRange,
    eventTypeFilter,
    sponsorshipFilter,
    locationFilter,
    sortConfig,
  ]);

  // Handle sorting
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Toggle column selection
  const handleColumnToggle = (columnId) => {
    if (selectedColumns.includes(columnId)) {
      if (selectedColumns.length > 1) {
        // Ensure at least one column is selected
        setSelectedColumns(selectedColumns.filter((id) => id !== columnId));
      }
    } else {
      setSelectedColumns([...selectedColumns, columnId]);
    }
  };

  // Export to CSV
  const csvData = useMemo(() => {
    // Create headers
    const headers = availableColumns
      .filter((col) => selectedColumns.includes(col.id))
      .map((col) => ({ label: col.label, key: col.id }));

    // Format data for CSV
    return {
      headers,
      data: filteredData.map((event) => {
        const row = {};
        selectedColumns.forEach((colId) => {
          if (colId === "event_type") {
            row[colId] = event.event_type?.label || "";
          } else if (colId === "sponsorship_status") {
            row[colId] = event.sponsorship_status?.label || "";
          } else if (colId === "day_event") {
            row[colId] = event.day_event ? "Yes" : "No";
          } else {
            row[colId] = event[colId] || "";
          }
        });
        return row;
      }),
    };
  }, [filteredData, selectedColumns, availableColumns]);

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = availableColumns
      .filter((col) => selectedColumns.includes(col.id))
      .map((col) => col.label);

    const tableRows = filteredData.map((event) => {
      return selectedColumns.map((colId) => {
        if (colId === "event_type") {
          return event.event_type?.label || "";
        } else if (colId === "sponsorship_status") {
          return event.sponsorship_status?.label || "";
        } else if (colId === "day_event") {
          return event.day_event ? "Yes" : "No";
        } else {
          return event[colId] || "";
        }
      });
    });

    doc.text("Event Report", 14, 16);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });
    doc.save("event_report.pdf");
  };

  // Handle view event details
  const handleViewEvent = (event) => {
    setCurrentRecord(event);
    setShowModal(true);
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm("");
    setDateRange([null, null]);
    setEventTypeFilter("all");
    setSponsorshipFilter("all");
    setLocationFilter("all");
    setSortConfig({ key: null, direction: null });
  };

  // Prepare chart data
  const chartData = useMemo(() => {
    if (chartType === "none") return [];

    // For event type distribution
    if (chartMetric === "event_type") {
      const typeCount = {};
      filteredData.forEach((event) => {
        const type = event.event_type?.label || "Unknown";
        typeCount[type] = (typeCount[type] || 0) + 1;
      });

      return Object.keys(typeCount).map((type) => ({
        name: type,
        value: typeCount[type],
      }));
    }

    // For sponsorship status distribution
    if (chartMetric === "sponsorship_status") {
      const statusCount = {};
      filteredData.forEach((event) => {
        const status = event.sponsorship_status?.label || "Unknown";
        statusCount[status] = (statusCount[status] || 0) + 1;
      });

      return Object.keys(statusCount).map((status) => ({
        name: status,
        value: statusCount[status],
      }));
    }

    // For location distribution
    if (chartMetric === "location") {
      const locationCount = {};
      filteredData.forEach((event) => {
        const location = event.event_location || "Unknown";
        locationCount[location] = (locationCount[location] || 0) + 1;
      });

      return Object.keys(locationCount).map((location) => ({
        name: location,
        value: locationCount[location],
      }));
    }

    return [];
  }, [filteredData, chartType, chartMetric]);

  // Chart colors
  const CHART_COLORS = [
    "#8884d8",
    "#83a6ed",
    "#8dd1e1",
    "#82ca9d",
    "#a4de6c",
    "#d0ed57",
    "#ffc658",
    "#ff8042",
    "#ff6361",
    "#bc5090",
  ];

  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      {/* Report Header */}
      <div className="mb-6">
        <h1 className="mb-2 text-2xl font-bold text-gray-800">
          Event Flexible Reports
        </h1>
        <p className="text-gray-600">
          Customize your event reports with various filters and display options
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button
          variant="contained"
          color="primary"
          startIcon={<FaFilter />}
          onClick={() => setShowFilters(!showFilters)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>

        <Button
          variant="outlined"
          color="primary"
          startIcon={<FaColumns />}
          onClick={() => setShowColumnSelector(!showColumnSelector)}
          className="text-blue-600 border-blue-600 hover:bg-blue-50"
        >
          Columns
        </Button>

        <CSVLink
          data={csvData.data}
          headers={csvData.headers}
          filename={"event_report.csv"}
          className="no-underline"
        >
          <Button
            variant="outlined"
            color="success"
            startIcon={<FaFileExport />}
            className="text-green-600 border-green-600 hover:bg-green-50"
          >
            Export CSV
          </Button>
        </CSVLink>

        <Button
          variant="outlined"
          color="secondary"
          startIcon={<FaDownload />}
          onClick={exportToPDF}
          className="text-purple-600 border-purple-600 hover:bg-purple-50"
        >
          Export PDF
        </Button>

        <Button
          variant="outlined"
          color="info"
          startIcon={<FaPrint />}
          onClick={() => window.print()}
          className="border-cyan-600 text-cyan-600 hover:bg-cyan-50"
        >
          Print
        </Button>

        <Button
          variant="outlined"
          color="warning"
          startIcon={<FaChartBar />}
          onClick={() =>
            setChartType(
              chartType === "none"
                ? "bar"
                : chartType === "bar"
                ? "pie"
                : "none"
            )
          }
          className="border-amber-600 text-amber-600 hover:bg-amber-50"
        >
          {chartType === "none"
            ? "Show Bar Chart"
            : chartType === "bar"
            ? "Show Pie Chart"
            : "Hide Chart"}
        </Button>
      </div>

      {/* Filters Section */}
      {showFilters && (
        <div className="p-4 mb-6 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Filters</h2>
            <Button
              size="small"
              onClick={resetFilters}
              startIcon={<FaTimes />}
              className="text-gray-600"
            >
              Reset
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Search */}
            <div>
              <TextField
                label="Search"
                variant="outlined"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <FaSearch className="mr-2 text-gray-400" />,
                }}
                size="small"
              />
            </div>

            {/* Date Range */}
            <div>
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                onChange={(dates) => setDateRange(dates)}
                format="MM-DD-YYYY"
                placeholder={["Start Date", "End Date"]}
              />
            </div>

            {/* Event Type Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Event Type</InputLabel>
              <Select
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
                label="Event Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                {uniqueEventTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Sponsorship Status Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Sponsorship Status</InputLabel>
              <Select
                value={sponsorshipFilter}
                onChange={(e) => setSponsorshipFilter(e.target.value)}
                label="Sponsorship Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                {uniqueSponsorshipStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Location Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Location</InputLabel>
              <Select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                label="Location"
              >
                <MenuItem value="all">All Locations</MenuItem>
                {uniqueLocations.map((location) => (
                  <MenuItem key={location} value={location}>
                    {location}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
      )}

      {/* Column Selector */}
      {showColumnSelector && (
        <div className="p-4 mb-6 border border-gray-200 rounded-lg bg-gray-50">
          <h2 className="mb-3 text-lg font-semibold text-gray-700">
            Select Columns to Display
          </h2>
          <div className="flex flex-wrap gap-2">
            {availableColumns.map((column) => (
              <Chip
                key={column.id}
                label={column.label}
                onClick={() => handleColumnToggle(column.id)}
                color={
                  selectedColumns.includes(column.id) ? "primary" : "default"
                }
                variant={
                  selectedColumns.includes(column.id) ? "filled" : "outlined"
                }
                className={`m-1 ${
                  selectedColumns.includes(column.id) ? "bg-blue-600" : ""
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Results Summary */}
      <div className="flex items-center justify-between mb-4">
        <Typography variant="body2" className="text-gray-600">
          Showing {filteredData.length} of {eventData.length} events
        </Typography>
        <div className="flex gap-2">
          {chartType !== "none" && (
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Chart Metric</InputLabel>
              <Select
                value={chartMetric}
                onChange={(e) => setChartMetric(e.target.value)}
                label="Chart Metric"
                size="small"
              >
                <MenuItem value="event_type">Event Type</MenuItem>
                <MenuItem value="sponsorship_status">
                  Sponsorship Status
                </MenuItem>
                <MenuItem value="location">Location</MenuItem>
              </Select>
            </FormControl>
          )}
          {Object.values(sortConfig).some(Boolean) && (
            <Chip
              label={`Sorted by: ${
                availableColumns.find((col) => col.id === sortConfig.key)?.label
              } (${sortConfig.direction})`}
              onDelete={() => setSortConfig({ key: null, direction: null })}
              size="small"
              className="text-blue-800 bg-blue-100"
            />
          )}
        </div>
      </div>

      {/* Chart Display */}
      {chartType !== "none" && chartData.length > 0 && (
        <div className="p-4 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-700">
            {chartMetric === "event_type"
              ? "Event Type Distribution"
              : chartMetric === "sponsorship_status"
              ? "Sponsorship Status Distribution"
              : "Location Distribution"}
          </h2>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {chartType === "bar" ? (
                <BarChart
                  data={chartData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 70 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    interval={0}
                  />
                  <YAxis />
                  <RechartsTooltip
                    formatter={(value) => [`${value} events`, "Count"]}
                  />
                  <Legend />
                  <Bar dataKey="value" name="Number of Events" fill="#8884d8" />
                </BarChart>
              ) : (
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value) => [`${value} events`, "Count"]}
                  />
                  <Legend />
                </PieChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Data Table */}
      <TableContainer component={Paper} className="mb-6 shadow-sm">
        <Table aria-label="event report table">
          <TableHead className="bg-gray-100">
            <TableRow>
              {availableColumns
                .filter((column) => selectedColumns.includes(column.id))
                .map((column) => (
                  <TableCell key={column.id} className="font-semibold">
                    <div
                      className="flex items-center cursor-pointer"
                      onClick={() => requestSort(column.id)}
                    >
                      {column.label}
                      {sortConfig.key === column.id ? (
                        sortConfig.direction === "ascending" ? (
                          <FaSortUp className="ml-1" />
                        ) : (
                          <FaSortDown className="ml-1" />
                        )
                      ) : (
                        <FaSort className="ml-1 text-gray-300" />
                      )}
                    </div>
                  </TableCell>
                ))}
              <TableCell className="font-semibold">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={selectedColumns.length + 1}
                  align="center"
                  className="py-8"
                >
                  <div className="flex items-center justify-center">
                    <div className="w-8 h-8 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                    <span className="ml-2">Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={selectedColumns.length + 1}
                  align="center"
                  className="py-8"
                >
                  <div className="text-center">
                    <FaSearch className="mx-auto mb-2 text-3xl text-gray-400" />
                    <Typography
                      variant="body1"
                      className="font-medium text-gray-500"
                    >
                      No events found
                    </Typography>
                    <Typography variant="body2" className="text-gray-400">
                      Try adjusting your search or filter criteria
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((event) => (
                <TableRow key={event.id} className="hover:bg-gray-50">
                  {selectedColumns.map((columnId) => (
                    <TableCell key={`${event.id}-${columnId}`}>
                      {columnId === "event_type" ? (
                        <Chip
                          label={event.event_type?.label || "N/A"}
                          size="small"
                          className={`${
                            event.event_type?.label === "Public"
                              ? "bg-green-100 text-green-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        />
                      ) : columnId === "sponsorship_status" ? (
                        <Chip
                          label={event.sponsorship_status?.label || "N/A"}
                          size="small"
                          className={`${
                            event.sponsorship_status?.label === "Open"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        />
                      ) : columnId === "day_event" ? (
                        event.day_event ? (
                          "Yes"
                        ) : (
                          "No"
                        )
                      ) : columnId === "title" ? (
                        <div className="font-medium">{event[columnId]}</div>
                      ) : (
                        event[columnId]
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewEvent(event)}
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        <FaEye />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Event Details Modal */}
      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <div className="p-4">
            <h2 className="mb-4 text-xl font-bold">
              {currentRecord.title || "Event Details"}
            </h2>
            <div className="flex flex-col gap-6 md:flex-row">
              <div className="md:w-1/3">
                {currentRecord.image ? (
                  <img
                    src={`${process.env.REACT_APP_API_URL}/uploads/${currentRecord.image}`}
                    alt={currentRecord.title}
                    className="w-full h-auto rounded-lg shadow-md"
                  />
                ) : (
                  <img
                    src={placeholderImage}
                    alt="Placeholder"
                    className="w-full h-auto bg-gray-100 rounded-lg shadow-md"
                  />
                )}
              </div>

              <div className="md:w-2/3">
                <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
                  <div>
                    <p className="text-sm text-gray-500">Event Type</p>
                    <p className="font-medium">
                      {currentRecord.event_type?.label || "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Sponsorship Status</p>
                    <p className="font-medium">
                      {currentRecord.sponsorship_status?.label || "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Start Date & Time</p>
                    <p className="font-medium">
                      {currentRecord.view_event_start_date}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">End Date & Time</p>
                    <p className="font-medium">
                      {currentRecord.view_event_end_date}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">
                      {currentRecord.event_location}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Contact</p>
                    <p className="font-medium">
                      {currentRecord.event_contact_number}
                    </p>
                  </div>
                </div>

                <Divider className="my-4" />

                <div>
                  <p className="mb-2 text-sm text-gray-500">Description</p>
                  <p>{currentRecord.description}</p>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default EventFlexiReportsChild;
